import StackTrace from "stacktrace-js";
import { reportError, type ErrorInfo } from "../api";

/**
 * Zentrale Diagnose für fehlgeschlagene Lookups (Foods, Purchases,
 * Availabilities, Array-/Map-Lookups). Ein Miss wird bewusst *nicht* als Fehler
 * behandelt – der Aufrufer bekommt weiterhin `undefined` – sondern zusätzlich an
 * `POST /api/error` gemeldet, inkl. aufgelöster Stacks (Aufbau + Zugriff) und
 * den Keys, aus denen der Lookup gebaut wurde.
 *
 * Schutzmechanismen gegen Telegram-Flutung: Dedup-Fenster pro Aufrufstelle und
 * ein globales Rate-Limit. Aktiv in prod/beta/dev, deaktiviert in Tests sowie im
 * Load-Delay-Modus der Race-Tests.
 */

export type LookupKind = "batch-cache" | "lookup-map" | "array-lookup";

export interface LookupMissContext {
  kind: LookupKind;
  cache: string;
  index?: string;
  requestedId: number;
  keys?: Record<string, unknown>;
  /** Stack der Stelle, an der der Lookup angefordert/aufgebaut wurde. */
  buildStack?: string;
  /** Stack der Stelle, an der auf den Lookup zugegriffen wurde. */
  accessStack?: string;
  /** Stack der React-Komponente, die den Lookup-Hook verwendet hat. */
  renderStack?: string;
  /** Ob der Server/die Quelle einen Eintrag geliefert hat (falls bekannt). */
  seeded?: boolean;
  /** Bereits bekannte Ids des Index (begrenzt, für den Kontext). */
  knownIndexIds?: number[];
}

const DEDUP_TTL_MS = 5 * 60_000;
const RATE_WINDOW_MS = 60_000;
const MAX_REPORTS_PER_WINDOW = 10;
const MAX_FRAMES = 20;
const MAX_STACK_CHARS = 4000;
const MAX_KEYS_CHARS = 1000;
const MAX_SEEN = 2000;

const VENDOR_FRAME =
  /(node_modules|react-dom|react-refresh|scheduler|@tanstack|vite\/|webpack)/i;

const APP_VERSION =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "unknown";

let runtimeEnabled: boolean | undefined;
let pendingBatchLoads = 0;
const seenAt = new Map<string, number>();
const reportTimes: number[] = [];

/**
 * Zählt laufende Batch-Ladevorgänge (Foods/Purchases/Availabilities). Array-
 * und Map-Lookups können „lädt noch" nicht von „nicht gefunden" unterscheiden –
 * solange etwas lädt, werden sie deshalb nicht als Miss gemeldet.
 */
export function beginBatchLoad(): void {
  pendingBatchLoads += 1;
}

export function endBatchLoad(): void {
  pendingBatchLoads = Math.max(0, pendingBatchLoads - 1);
}

export function areLookupsSettled(): boolean {
  return pendingBatchLoads === 0;
}

/** Runtime-Override (Tests, manuelles Debugging). `undefined` = Default-Gate. */
export function setLookupDiagnosticsEnabled(enabled: boolean | undefined): void {
  runtimeEnabled = enabled;
}

/** Setzt Dedup-/Rate-Limit-Zustand zurück (nur für Tests). */
export function resetLookupDiagnostics(): void {
  runtimeEnabled = undefined;
  pendingBatchLoads = 0;
  seenAt.clear();
  reportTimes.length = 0;
}

export function lookupDiagnosticsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  if (runtimeEnabled !== undefined) return runtimeEnabled;
  if (import.meta.env.MODE === "test") return false;
  if (import.meta.env.VITE_LOOKUP_DIAGNOSTICS === "false") return false;
  const globalWindow = window as unknown as { __P2P_LOAD_DELAY_MS__?: unknown };
  const delay = Number(globalWindow.__P2P_LOAD_DELAY_MS__);
  if (Number.isFinite(delay) && delay > 0) return false;
  return true;
}

/** Nur positive, endliche Ids sind meldbar (Sentinels wie `-1` ausschließen). */
export function isReportableId(
  id: number | null | undefined,
): id is number {
  return id != null && Number.isFinite(id) && id > 0;
}

/** Roh-Stack am aktuellen Punkt; `undefined`, wenn Diagnose inaktiv ist. */
export function captureStack(): string | undefined {
  if (!lookupDiagnosticsEnabled()) return undefined;
  try {
    return new Error("lookup-diagnostics").stack;
  } catch {
    return undefined;
  }
}

/** Dedup + Rate-Limit. `false` = verwerfen. */
function takeRateLimitSlot(signature: string): boolean {
  const now = Date.now();
  const last = seenAt.get(signature);
  if (last !== undefined && now - last < DEDUP_TTL_MS) return false;

  while (reportTimes.length > 0 && now - reportTimes[0] > RATE_WINDOW_MS) {
    reportTimes.shift();
  }
  if (reportTimes.length >= MAX_REPORTS_PER_WINDOW) return false;

  seenAt.set(signature, now);
  reportTimes.push(now);
  if (seenAt.size > MAX_SEEN) pruneSeen(now);
  return true;
}

function pruneSeen(now: number): void {
  for (const [key, ts] of seenAt) {
    if (now - ts >= DEDUP_TTL_MS) seenAt.delete(key);
  }
  while (seenAt.size > MAX_SEEN) {
    const oldest = seenAt.keys().next().value;
    if (oldest === undefined) break;
    seenAt.delete(oldest);
  }
}

function signatureOf(context: LookupMissContext): string {
  const stack =
    context.accessStack ?? context.renderStack ?? context.buildStack ?? "";
  const firstFrame =
    stack.split("\n").find((line) => line.trim().startsWith("at ")) ?? "";
  return `${context.kind}|${context.cache}|${context.index ?? ""}|${
    context.requestedId
  }|${firstFrame.trim()}`;
}

async function resolveStack(raw?: string): Promise<string | undefined> {
  if (!raw) return undefined;
  try {
    const error = new Error("lookup-diagnostics");
    error.stack = raw;
    const frames = await StackTrace.fromError(error, { offline: false });
    const relevant = frames.filter(
      (frame) => !VENDOR_FRAME.test(frame.fileName ?? ""),
    );
    const chosen = (relevant.length > 0 ? relevant : frames).slice(
      0,
      MAX_FRAMES,
    );
    return cap(
      chosen.map((frame) => frame.toString()).join("\n"),
      MAX_STACK_CHARS,
    );
  } catch {
    return cap(raw, MAX_STACK_CHARS);
  }
}

function cap(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}\n… (gekürzt)`;
}

function stringifyKeys(keys?: Record<string, unknown>): string | undefined {
  if (!keys) return undefined;
  try {
    return cap(JSON.stringify(keys), MAX_KEYS_CHARS);
  } catch {
    return undefined;
  }
}

/**
 * Meldet einen Lookup-Miss. Fire-and-forget: wirft nie und blockiert den
 * Aufrufer nicht. Gibt dennoch ein Promise zurück (Tests können awaiten).
 */
export async function reportLookupMiss(
  context: LookupMissContext,
): Promise<void> {
  if (!lookupDiagnosticsEnabled()) return;
  if (!isReportableId(context.requestedId)) return;
  if (
    (context.kind === "array-lookup" || context.kind === "lookup-map") &&
    !areLookupsSettled()
  ) {
    return;
  }
  if (!takeRateLimitSlot(signatureOf(context))) return;

  try {
    const [requestStack, accessStack, renderStack] = await Promise.all([
      resolveStack(context.buildStack),
      resolveStack(context.accessStack),
      resolveStack(context.renderStack),
    ]);

    const info: ErrorInfo = {
      error: `Lookup-Miss [${context.kind}] ${context.cache}${
        context.index ? `#${context.index}` : ""
      } id=${context.requestedId}`,
      kind: context.kind,
      cache: context.cache,
      index: context.index,
      requestedId: context.requestedId,
      keys: stringifyKeys(context.keys),
      requestStack,
      accessStack,
      renderStack,
      seeded: context.seeded,
      knownIndexIds: context.knownIndexIds?.join(","),
      appVersion: APP_VERSION,
      url: typeof window !== "undefined" ? window.location.href : undefined,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      occurredAt: new Date().toISOString(),
    };

    await reportError({ body: info });
  } catch {
    // Die Diagnose darf nie selbst zum Fehler werden.
  }
}

type AnyLookup = Record<string | number, unknown>;

/**
 * Umhüllt einen bereits gebauten Lookup (Id -> Entity) mit einem Proxy, der
 * Zugriffe auf fehlende numerische Keys meldet. Nicht-numerische Zugriffe
 * (z. B. `then`, `toJSON`, Symbole) werden ignoriert.
 */
export function trackLookupAccess<T extends AnyLookup>(
  lookup: T,
  cache: string,
): T {
  if (!lookupDiagnosticsEnabled()) return lookup;
  return new Proxy(lookup, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (value === undefined && typeof prop === "string" && /^\d+$/.test(prop)) {
        const id = Number(prop);
        if (id > 0) {
          const knownIndexIds = Object.keys(target)
            .map((key) => Number(key))
            .filter((key) => Number.isFinite(key))
            .slice(0, 50);
          void reportLookupMiss({
            kind: "lookup-map",
            cache,
            requestedId: id,
            accessStack: captureStack(),
            knownIndexIds,
          });
        }
      }
      return value;
    },
  }) as T;
}
